import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import {
  Users, ListChecks, CheckCircle2, XCircle, Coins, TrendingUp, RotateCcw, Clock, Activity,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import { STATUS_TOKENS } from "@/components/StatusBadge";
import { AdminQuickActions } from "@/components/AdminQuickActions";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="rounded-lg border p-5 animate-fade-up"
    style={{ backgroundColor: "#111827", borderColor: "#1E293B" }}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div className="font-heading text-3xl font-bold text-slate-100">{value}</div>
  </div>
);

export default function Dashboard() {
  const { data: s } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
    refetchInterval: 5000,
  });

  const chartData = s ? Object.entries(s.by_status).map(([k, v]) => ({
    name: STATUS_TOKENS[k]?.label || k, value: v, color: STATUS_TOKENS[k]?.text || "#94A3B8",
  })) : [];

  return (
    <Layout>
      <PageHeader title="Panel de Control" subtitle="Monitoreo en tiempo real del sistema" />
      <div className="p-8 space-y-6">
        <AdminQuickActions />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="stat-users" icon={Users} label="Usuarios" value={s?.users_total ?? "—"} accent="#C2620E" />
          <StatCard testid="stat-jobs" icon={ListChecks} label="Trabajos totales" value={s?.jobs_total ?? "—"} accent="#C2620E" />
          <StatCard testid="stat-active" icon={Activity} label="Activos ahora" value={s?.active ?? "—"} accent="#818CF8" />
          <StatCard testid="stat-avg" icon={Clock} label="Tiempo prom. (s)" value={s?.avg_duration ?? "—"} accent="#F59E0B" />
          <StatCard testid="stat-completed" icon={CheckCircle2} label="Completados" value={s?.completed ?? "—"} accent="#34D399" />
          <StatCard testid="stat-failed" icon={XCircle} label="Fallidos" value={s?.failed ?? "—"} accent="#F87171" />
          <StatCard testid="stat-granted" icon={Coins} label="Créditos otorgados" value={s?.credits_granted ?? "—"} accent="#F59E0B" />
          <StatCard testid="stat-used" icon={TrendingUp} label="Créditos usados" value={s?.credits_used ?? "—"} accent="#FF4D4D" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-lg border p-6" style={{ backgroundColor: "#111827", borderColor: "#1E293B" }}>
            <h3 className="font-heading font-semibold text-slate-200 mb-4">Trabajos por estado</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 40 }}>
                <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fill: "#64748B", fontSize: 10 }}
                  interval={0} axisLine={{ stroke: "#1E293B" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={{ stroke: "#1E293B" }} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: "#0D111A", border: "1px solid #1E293B", borderRadius: 8, color: "#F8FAFC" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((e) => <Cell key={e.name} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-lg border p-6" style={{ backgroundColor: "#111827", borderColor: "#1E293B" }}>
            <h3 className="font-heading font-semibold text-slate-200 mb-4">Créditos</h3>
            <div className="space-y-4">
              {[
                { l: "Otorgados", v: s?.credits_granted, c: "#F59E0B", icon: Coins },
                { l: "Utilizados", v: s?.credits_used, c: "#FF4D4D", icon: TrendingUp },
                { l: "Devueltos", v: s?.credits_refunded, c: "#34D399", icon: RotateCcw },
              ].map((x) => (
                <div key={x.l} className="flex items-center justify-between py-2 border-b" style={{ borderColor: "#1E293B" }}>
                  <span className="flex items-center gap-2 text-sm text-slate-400">
                    <x.icon className="w-4 h-4" style={{ color: x.c }} /> {x.l}
                  </span>
                  <span className="font-mono font-semibold" style={{ color: x.c }}>{x.v ?? "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
